import { styled } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Button, { IconButton, SplitButton } from "@atlaskit/button-next/new";
import DropdownMenu, {
    DropdownItem,
    DropdownItemGroup
} from "@atlaskit/dropdown-menu";
import BoardIcon from "@atlaskit/icon/core/board";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import { Inline, Text, xcss } from "@atlaskit/primitives-next";
import { token } from "@atlaskit/tokens";

import { useMigrationsManagementUIAnalytics } from "../../hooks";
import { messages } from "../messages";
import {
    JiraBoardEmbedState,
    JiraBoardOption,
    CustomerDomainJiraBoardEmbedType
} from "../types";

const BorderlessButtonWrapper = styled.div({
    // this is necessary to override atlaskit button styles to remove borders when selected
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        "--ds-border-selected": "transparent",
        "--ds-border-brand": "transparent",
        "--ds-surface-selected": "transparent",
        "borderColor": "transparent !important",
        "borderWidth": "0 !important"
    }
});

const toggleButtonGroupStyles = xcss({
    // add border
    borderRadius: "border.radius.100",
    border: `1px solid ${token("color.border")}`,
    padding: "space.050"
});

export const BoardAndEmbedViewOptions = ({
    boards,
    selectedBoard,
    setSelectedBoard
}: {
    boards: JiraBoardEmbedState[];
    selectedBoard: JiraBoardEmbedState | null;
    setSelectedBoard: (board: JiraBoardEmbedState | null) => void;
}) => {
    const intl = useIntl();
    const { sendEmbedViewToggleUIEvent, sendBoardSelectionUIEvent } =
        useMigrationsManagementUIAnalytics();

    const boardOptions: JiraBoardOption[] = boards.map((boardState) => ({
        label: boardState.board.boardName,
        value: boardState.board.boardId,
        data: boardState
    }));

    const handleSelectedBoardChange = (option: JiraBoardOption | null) => {
        if (option) {
            setSelectedBoard(option.data);
            sendBoardSelectionUIEvent(
                option.data.projectKey,
                option.data.board.boardId
            );
        }
    };

    const setSelectedView = (embedType: CustomerDomainJiraBoardEmbedType) => {
        // if only one view is present, there is nothing to toggle
        if (
            !selectedBoard ||
            !selectedBoard.calendarUrl ||
            !selectedBoard.timelineUrl
        ) {
            return;
        }

        // toggle the isSelected state of timeline and calendar
        const updatedBoard: JiraBoardEmbedState = {
            ...selectedBoard,
            selectedView: embedType
        };

        setSelectedBoard(updatedBoard);
        sendEmbedViewToggleUIEvent(embedType);
    };

    return (
        <Inline space="space.100" alignInline="end" alignBlock="center">
            <BorderlessButtonWrapper>
                <Inline
                    xcss={toggleButtonGroupStyles}
                    space="space.025"
                    alignBlock="center"
                >
                    <Button
                        appearance="subtle"
                        isDisabled={!selectedBoard?.timelineUrl}
                        isSelected={
                            selectedBoard?.selectedView ===
                            CustomerDomainJiraBoardEmbedType.TIMELINE
                        }
                        spacing="compact"
                        onClick={() =>
                            // alter the isSelected state of timeline and calendar
                            setSelectedView(
                                CustomerDomainJiraBoardEmbedType.TIMELINE
                            )
                        }
                    >
                        <FormattedMessage
                            {...messages.timelineViewToggleLabel}
                        />
                    </Button>

                    <Button
                        appearance="subtle"
                        isDisabled={!selectedBoard?.calendarUrl}
                        isSelected={
                            selectedBoard?.selectedView ===
                            CustomerDomainJiraBoardEmbedType.CALENDAR
                        }
                        spacing="compact"
                        onClick={() =>
                            setSelectedView(
                                CustomerDomainJiraBoardEmbedType.CALENDAR
                            )
                        }
                    >
                        <FormattedMessage
                            {...messages.calendarViewToggleLabel}
                        />
                    </Button>
                </Inline>
            </BorderlessButtonWrapper>

            <SplitButton>
                <Button iconBefore={BoardIcon}>
                    {selectedBoard?.board.boardName}
                </Button>
                <DropdownMenu<HTMLButtonElement>
                    shouldRenderToParent
                    // this is the recommended approach from atlaskit
                    // eslint-disable-next-line react/no-unstable-nested-components
                    trigger={({ triggerRef, ...triggerProps }) => (
                        <IconButton
                            ref={triggerRef}
                            {...triggerProps}
                            icon={ChevronDownIcon}
                            label={intl.formatMessage(
                                messages.selectBoardSplitButtonDropdownLabel
                            )}
                        />
                    )}
                >
                    <DropdownItemGroup>
                        {boardOptions.map((option) => (
                            <DropdownItem
                                key={option.value}
                                onClick={() =>
                                    handleSelectedBoardChange(option)
                                }
                                isSelected={
                                    selectedBoard?.board.boardId ===
                                    option.value
                                }
                            >
                                <Text>{option.label}</Text>
                            </DropdownItem>
                        ))}
                    </DropdownItemGroup>
                </DropdownMenu>
            </SplitButton>
        </Inline>
    );
};
